import React, { useEffect, useState } from 'react';

import { Vote } from '../utils/types';

interface VotingResultsProps {
    votes: { [userId: string]: Vote } | undefined;
    revealed: boolean;
}

const VotingResults: React.FC<VotingResultsProps> = ({ votes, revealed }) => {
    const [showAnimation, setShowAnimation] = useState(false);

    useEffect(() => {
        if (revealed) {
            setShowAnimation(true);
            const timer = setTimeout(() => {
                setShowAnimation(false);
            }, 1000);
            return () => clearTimeout(timer);
        }
    }, [revealed]);

    if (!votes || !revealed) {
        return null;
    }

    const voteValues = Object.values(votes).map((vote) => vote.value);

    // Calculate vote statistics (excluding '?' votes)
    const numericVotes = voteValues
        .filter((value) => value !== '?' && !isNaN(Number(value)))
        .map((value) => Number(value));

    const average =
        numericVotes.length > 0
            ? numericVotes.reduce((sum, val) => sum + val, 0) /
              numericVotes.length
            : 0;

    // Count occurrences of each value
    const voteCounts: { [value: string]: number } = {};
    voteValues.forEach((value) => {
        voteCounts[value] = (voteCounts[value] || 0) + 1;
    });

    // Sort vote values for display
    const sortedVotes = Object.entries(voteCounts).sort((a, b) => {
        // Sort '?' to the end
        if (a[0] === '?') return 1;
        if (b[0] === '?') return -1;

        // Sort remaining values numerically if possible
        const aNum = parseFloat(a[0]);
        const bNum = parseFloat(b[0]);

        if (!isNaN(aNum) && !isNaN(bNum)) {
            return aNum - bNum;
        }

        // Otherwise sort alphabetically
        return a[0].localeCompare(b[0]);
    });

    // Find the most voted value(s)
    const maxVotes = Math.max(...Object.values(voteCounts));
    const consensusValues = Object.entries(voteCounts)
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        .filter(([_, count]) => count === maxVotes)
        .map(([value]) => value);

    // Check if there is consensus (more than 50% agree)
    const totalVotes = voteValues.length;
    const hasConsensus = maxVotes > totalVotes / 2;

    return (
        <div
            className={`
            my-6 p-6 rounded-lg bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 shadow-md
            ${showAnimation ? 'animate-reveal' : ''}
        `}
        >
            <h3 className="text-lg font-semibold mb-4 text-gray-700 dark:text-gray-300 flex items-center">
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 mr-2"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                    />
                </svg>
                Voting Results
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Vote summary */}
                <div className="bg-white dark:bg-dark-card p-4 rounded-lg shadow-sm">
                    <div className="mb-4">
                        <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-500 dark:text-gray-400">
                                Average Score
                            </span>
                            <span className="text-2xl font-bold text-primary-dark dark:text-primary-light">
                                {average.toFixed(1)}
                            </span>
                        </div>
                        <div className="mt-1 h-1 w-full bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                            <div
                                className="bg-primary h-full rounded-full transition-all duration-1000 ease-out"
                                style={{
                                    width: numericVotes.length
                                        ? `${Math.min(100, (average / 15) * 100)}%`
                                        : '0%',
                                }}
                            ></div>
                        </div>
                    </div>

                    <div>
                        <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-500 dark:text-gray-400">
                                Consensus
                            </span>
                            <div>
                                {hasConsensus ? (
                                    <span className="inline-flex items-center text-sm font-medium text-green-600 dark:text-green-400">
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="h-4 w-4 mr-1"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M5 13l4 4L19 7"
                                            />
                                        </svg>
                                        {consensusValues
                                            .map((v) =>
                                                v.length > 8
                                                    ? v.substring(0, 8) + '...'
                                                    : v,
                                            )
                                            .join(', ')}
                                    </span>
                                ) : (
                                    <span className="inline-flex items-center text-sm font-medium text-amber-600 dark:text-amber-400">
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="h-4 w-4 mr-1"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                                            />
                                        </svg>
                                        No Consensus
                                    </span>
                                )}
                            </div>
                        </div>
                        <div className="mt-1 h-1 w-full bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                            <div
                                className={`h-full rounded-full transition-all duration-1000 ease-out ${hasConsensus ? 'bg-green-500' : 'bg-amber-500'}`}
                                style={{
                                    width: `${(maxVotes / totalVotes) * 100}%`,
                                }}
                            ></div>
                        </div>
                    </div>
                </div>

                {/* Vote distribution */}
                <div className="flex flex-wrap gap-2 justify-center items-center">
                    {sortedVotes.map(([value, count]) => {
                        const isConsensus = consensusValues.includes(value);
                        const isQuestionMark = value === '?';

                        return (
                            <div
                                key={value}
                                className={`
                                    relative flex flex-col items-center p-3 rounded-lg 
                                    ${
                                        isConsensus
                                            ? 'bg-primary-light text-white'
                                            : isQuestionMark
                                              ? 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                                              : 'bg-white dark:bg-dark-card text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-dark-border'
                                    }
                                    ${showAnimation ? 'animate-pop-in' : ''}
                                    shadow-sm transition-all duration-200
                                `}
                                style={{
                                    animationDelay: `${parseInt(value) * 50}ms`,
                                    minWidth: '4rem',
                                }}
                            >
                                <span
                                    className={`
                                    absolute -top-2 -right-2 
                                    ${count > 1 ? 'flex' : 'hidden'} 
                                    items-center justify-center 
                                    w-5 h-5 text-xs font-bold rounded-full
                                    ${
                                        isConsensus
                                            ? 'bg-white text-primary-dark'
                                            : 'bg-primary-light text-white'
                                    }
                                `}
                                >
                                    {count}
                                </span>
                                <div
                                    className="text-2xl font-bold text-center"
                                    title={value}
                                >
                                    {value.length > 6
                                        ? value.substring(0, 6) + '...'
                                        : value}
                                </div>
                                <div className="text-xs mt-1 opacity-80">
                                    {count === 1 ? '1 vote' : `${count} votes`}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default VotingResults;
